import React from "react";
import { motion } from "framer-motion";
import { Music2, Sparkles, Flame, Rocket, Disc3 } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHeader, Section, Reveal, Eyebrow, H2 } from "@/components/Section";

const timeline = [
  {
    year: "2025 · Q2",
    title: "The First Chapter — Hanuman Chalisa (Aggressive)",
    body: "KASHiX debuts with a cinematic, aggressive reimagining of the eternal Chalisa. The template is set: ancient in intent, modern in execution.",
    Icon: Flame,
  },
  {
    year: "2025 · Q2",
    title: "Shiv Tandav Stotram",
    body: "The Tandav is rebuilt as a thunderous cinematic hymn — Sanskrit fire meets 808 bass. The KASHiX sound crystallises.",
    Icon: Sparkles,
  },
  {
    year: "2025 · Q2–Q3",
    title: "Shivashtakam · Kaal Bhairav · NA YUDH THA NA VIJAY",
    body: "Sanskrit stotras and mythic narrations enter the catalog. Devotional music expands into an entire cinematic universe.",
    Icon: Music2,
  },
  {
    year: "2025 · Q3",
    title: "The Meditation Chapter",
    body: "Hare Krishna, Hare Ram, Mahamrityunjay Mantra and Hanuman Beej Mantra arrive — slow, breath-paced loops for stillness and inner power.",
    Icon: Disc3,
  },
  {
    year: "2026 →",
    title: "The Digital Temple",
    body: "The kashixmusic.com launch — the official digital home. New releases, licensing catalogs, and immersive experiences follow monthly.",
    Icon: Rocket,
  },
];

const milestones = [
  { k: "12+", label: "Original Releases" },
  { k: "2", label: "Sacred Languages · Sanskrit · Hindi" },
  { k: "6", label: "Sonic Categories" },
  { k: "∞", label: "The infinite loop of बीज मंत्र" },
];

const About = () => (
  <>
    <SEO
      title="About KASHiX"
      description="The story of KASHiX — mission, vision, philosophy, timeline, discography milestones and future vision."
    />
    <PageHeader
      eyebrow="The Artist"
      hindi="कलाकार की यात्रा"
      title="KASHiX — Where Devotion Becomes Power"
      subtitle="An independent Indian artist rebuilding the sound of devotion for a new generation, without breaking the thread of tradition."
    />

    {/* MISSION · VISION · PHILOSOPHY */}
    <Section className="!py-16">
      <div className="grid gap-6 md:grid-cols-3">
        {[
          { eyebrow: "Mission", hindi: "उद्देश्य", title: "Rebuild the sound of devotion.", body: "Give the next generation a devotional catalog they can feel proud of pressing play on — production that stands on the world's biggest stages, wrapped around Sanskrit their grandmothers would recognise." },
          { eyebrow: "Vision", hindi: "दृष्टिकोण", title: "A digital temple, open to all.", body: "Devotional music is not nostalgic wallpaper or new-age tourism. It is contemporary art with ancient roots. KASHiX is building the digital temple where that meets the modern soul." },
          { eyebrow: "Philosophy", hindi: "दर्शन", title: "Production is prayer.", body: "Every mix, every take, every layer is offered like a ritual. Sanskrit precision. Cinematic dynamics. Zero shortcuts. The devotional intent is the master, not the market." },
        ].map((c, i) => (
          <Reveal key={c.eyebrow} delay={i * 0.1}>
            <div className="glass rounded-2xl p-8 h-full">
              <Eyebrow>{c.eyebrow}</Eyebrow>
              <div className="mt-4 font-hindi text-xl text-[#FF9933]">{c.hindi}</div>
              <div className="mt-1 font-display text-2xl text-white leading-tight tracking-tight">{c.title}</div>
              <p className="mt-4 text-white/70 leading-relaxed text-sm">{c.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>

    {/* STORY */}
    <Section className="!py-8">
      <div className="grid gap-14 lg:grid-cols-12 items-center">
        <Reveal>
          <div className="lg:col-span-7">
            <Eyebrow>The Story of KASHiX</Eyebrow>
            <div className="mt-5"><H2 hindi="कहानी">Why devotional music. Why now.</H2></div>
            <div className="mt-8 space-y-6 text-white/75 leading-relaxed">
              <p>
                Devotional music in India was either fossilised into nostalgia or diluted into wellness wallpaper.
                KASHiX rejected both. The starting brief was simple: what if Shiva, Hanuman and the Sanskrit stotras
                came back on the same production floor as Zimmer, A24 and Dolby?
              </p>
              <p>
                Every track begins in scripture. It is recorded with the seriousness of a stotra path and produced
                with the ambition of a film score. The Chalisa was rebuilt from the ground up. The Tandav was given
                a bass floor deep enough for a stadium. The Beej mantras were treated with the reverence they deserve
                and the arrangement they never had.
              </p>
              <p>
                The result: devotional music that does not ask permission. Aggressive when the deity is fierce.
                Still when the mantra is deep. Cinematic always.
              </p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-white/5">
              <img src="https://images.unsplash.com/photo-1768843883385-11ded8a0d016?w=1400&q=80" alt="Temple silhouette" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10 mix-blend-multiply" />
              <div className="absolute inset-0 grain" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-hindi text-2xl text-white/90">महाकाल की ध्वनि</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">The Sound of Mahakal</div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>

    {/* MILESTONES */}
    <Section className="!py-16">
      <Reveal>
        <Eyebrow>Discography milestones</Eyebrow>
        <div className="mt-4"><H2 hindi="उपलब्धियाँ">A first year built with fire.</H2></div>
      </Reveal>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {milestones.map((m, i) => (
          <Reveal key={m.label} delay={i * 0.08}>
            <div className="glass rounded-2xl p-8 text-center">
              <div className="font-display text-5xl md:text-6xl text-[#FF9933]">{m.k}</div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.3em] text-white/60">{m.label}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>

    {/* TIMELINE */}
    <Section className="!py-16">
      <Reveal>
        <Eyebrow>Timeline</Eyebrow>
        <div className="mt-4"><H2 hindi="यात्रा">The journey so far.</H2></div>
      </Reveal>
      <div className="mt-14 relative">
        <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#FF9933]/40 to-transparent" />
        <div className="space-y-10">
          {timeline.map((t, i) => (
            <motion.div
              key={t.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className={`relative md:grid md:grid-cols-2 md:gap-10 ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}
            >
              <div className={`pl-16 md:pl-0 ${i % 2 ? "md:pl-12 md:pr-0 md:text-left" : "md:pr-12 md:text-right"}`}>
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">{t.year}</div>
                <h3 className="mt-2 font-display text-2xl md:text-3xl text-white tracking-tight">{t.title}</h3>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">{t.body}</p>
              </div>
              <div className="hidden md:block" />
              <div className="absolute left-0 md:left-1/2 top-0 md:-translate-x-1/2 w-12 h-12 rounded-full bg-[#0A0A0A] border border-[#FF9933]/60 flex items-center justify-center saffron-glow">
                <t.Icon size={16} className="text-[#FF9933]" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>

    {/* FUTURE VISION */}
    <Section className="!py-20">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl glass-strong p-10 md:p-14">
          <div className="absolute inset-0 opacity-40 pointer-events-none"
               style={{ background: "radial-gradient(60% 80% at 50% 0%, rgba(255,153,51,0.25), transparent 70%)" }} />
          <div className="relative grid gap-10 lg:grid-cols-12 items-center">
            <div className="lg:col-span-7">
              <Eyebrow>Future Vision</Eyebrow>
              <div className="mt-4 font-hindi text-2xl md:text-3xl text-[#FF9933]">भविष्य की भक्ति</div>
              <h2 className="mt-1 font-display text-4xl md:text-5xl text-white tracking-tight">Devotion, at cinematic scale.</h2>
              <p className="mt-5 text-white/70 leading-relaxed">
                Concept albums for each deity. Film scoring collaborations. Immersive Dolby Atmos temple experiences.
                Sanskrit preserved with the ambition of a global label. The KASHiX catalog is being built as the
                cinematic canon of Indian devotional music for the next decade.
              </p>
            </div>
            <div className="lg:col-span-5 grid grid-cols-2 gap-3">
              {["Concept Albums", "Immersive Atmos", "Film Scoring", "Live Cinematic"].map((k) => (
                <div key={k} className="rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Chapter</div>
                  <div className="mt-2 text-white text-sm">{k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </Section>
  </>
);

export default About;
